import React, { useMemo } from "react";

import {
  faCalendarClock,
  faQuestion,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { endOfToday, startOfToday, subYears } from "date-fns";

import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import {
  CaseEnvironment,
  EventViewerCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { TruncatedIdPill } from "src/decisionsOverview/TruncatedIdPill";
import { EmptyState } from "src/design-system/EmptyState";
import { Cardinality, useEntitySchemas } from "src/entities/queries";
import { useEvent, useEventSchema } from "src/eventsCatalogue/queries";
import { RelationIcon } from "src/eventsCatalogue/SchemaEditor/RelationIcons";
import { PROPERTY_TYPES_TO_ICONS } from "src/eventsCatalogue/SchemaEditor/utils";
import { renderEventPropertyValue } from "src/eventsCatalogue/utils/renderEventValue";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { TYPE_ICONS } from "src/utils/constants";

type EventViewerBlockProps = Omit<CaseBlockProps, "children"> & {
  data: EventViewerCaseBlockConfigAttributes;
  caseEnv?: CaseEnvironment;
};

export const EventDetailsBlock: React.FC<EventViewerBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const { workspace } = useWorkspaceContext();

  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";

  const { data: entitySchemas, isLoading: isLoadingEntitySchemas } =
    useEntitySchemas({
      baseUrl: workspace.base_url ?? "",
    });

  const { data: eventSchema, isLoading: isLoadingSchema } = useEventSchema(
    data.event.type,
  );

  // Use a wide time window to ensure we can find the event
  const timeWindow = useMemo(
    () => ({
      from: subYears(startOfToday(), 1).toISOString(),
      to: endOfToday().toISOString(),
    }),
    [],
  );

  const {
    data: event,
    isLoading: isLoadingEvent,
    isEnabled,
  } = useEvent(
    {
      eventType: data.event.type,
      eventId: data.event.id,
      environment: env,
      timeWindow,
    },
    {
      enabled: !!data.event.id,
    },
  );

  const items: PropertiesListItem[] = useMemo(() => {
    if (!event?.payload || !eventSchema) return [];

    const eventData = event.payload;
    const rows: PropertiesListItem[] = [];

    rows.push({
      key: "id",
      label: "ID",
      value: <TruncatedIdPill id={eventData.id} />,
      icon: TYPE_ICONS.string || faQuestion,
    });

    Object.entries(eventSchema.properties)
      .filter(([key]) => !["id"].includes(key))
      .forEach(([key, propertyDef]) => {
        const cellValue = eventData[key];
        if (cellValue === undefined || cellValue === null) return;

        const icon =
          propertyDef.type === PropertyDefinitionOutputTypeEnum.RELATION ? (
            <RelationIcon cardinality={Cardinality.ONE} size="2xs" />
          ) : (
            PROPERTY_TYPES_TO_ICONS[
              propertyDef.type as keyof typeof PROPERTY_TYPES_TO_ICONS
            ] || faQuestion
          );

        const renderedValue = renderEventPropertyValue(
          cellValue,
          propertyDef,
          caseEnv as Environment,
          entitySchemas,
          key,
        );

        rows.push({
          key,
          label:
            propertyDef.display_name ??
            propertyDef.relation_schema?.entity_type ??
            key,
          value: renderedValue,
          icon,
        });
      });

    return rows;
  }, [event, eventSchema, entitySchemas, caseEnv]);

  const isLoading = isLoadingSchema || isLoadingEvent || isLoadingEntitySchemas;

  if (!isEnabled) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="event-details-no-event-id"
          description="This block currently has no linked event."
          headline="No event provided"
          icon={faSearch}
          size="sm"
        />
      </CaseBlock>
    );
  }

  if (!isLoading && !event) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          description="Unable to load event data"
          headline="Error loading event"
          icon={faCalendarClock}
          size="sm"
          variant="error"
        />
      </CaseBlock>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <CaseBlock {...props} isLoading={false}>
        <EmptyState
          description="This event has no data to display"
          headline="No data available"
          icon={faCalendarClock}
          size="sm"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock {...props} isLoading={isLoading}>
      <PropertiesList
        dataLocPrefix="event-viewer-block"
        isLoading={isLoading}
        items={items}
        showIcons={true}
      />
    </CaseBlock>
  );
};
